import axios from 'axios';
import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import ReviewForm from './ReviewForm';
import ReviewModal from './ReviewModal';
import { initStarsParallax, destroyStarsParallax } from '../utils/starsParallax';

const CommunityCardItem = ({ card }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [isFollowing, setIsFollowing] = useState(card.isFollowing);
  const [followLoading, setFollowLoading] = useState(false);
  
  // Ref for card to attach parallax animation
  const cardRef = useRef(null);
  const parallaxInstanceRef = useRef(null);

  const handleFollow = async (e) => {
    e.stopPropagation();
    if (!user) {
      navigate('/login');
      return;
    }

    if (card.isSelf) return;

    try {
      setFollowLoading(true);
      if (isFollowing) {
        await axios.delete(`/api/follow/${card.userId._id}/unfollow`);
        setIsFollowing(false);
      } else {
        await axios.post(`/api/follow/${card.userId._id}/follow`);
        setIsFollowing(true);
      }
    } catch (error) {
      console.error('Follow error:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleReviewSuccess = () => {
    setShowReviewForm(false);
    setEditingReview(null);
  };

  const handleEditClick = (review) => {
    setEditingReview(review);
    setShowReviewForm(true);
  };

  const renderStarRating = (rating) => {
    if (!rating || rating === 0) return null;

    return (
      <div className="average-rating">
        <div className="star-rating-display">
          {[1, 2, 3, 4, 5].map((star) => (
            <span
              key={star}
              className={`star ${star <= Math.round(rating) ? 'filled' : 'empty'}`}
            >
              ★
            </span>
          ))}
        </div>
        <span className="rating-text">
          {rating.toFixed(1)} ({card.totalReviews || 0} {card.totalReviews === 1 ? 'review' : 'reviews'})
        </span>
      </div>
    );
  };

  // Helper function to get image URL (handles both Cloudinary and legacy GridFS)
  const getImageUrl = (avatar) => {
    if (!avatar) return null;
    // If it's already a full URL (Cloudinary), use it directly
    if (avatar.startsWith('http://') || avatar.startsWith('https://')) {
      return avatar;
    }
    // Otherwise, it's a GridFS ID (legacy), use the media endpoint
    return `/api/media/${avatar}`;
  };

  // Helper function to ensure URL has proper protocol
  const ensureProtocol = (url) => {
    if (!url) return '#';
    // If URL already has a protocol, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Otherwise, add https:// prefix
    return `https://${url}`;
  };

  // Get username from populated userId field
  const username = card.userId?.username || card.username;
  
  // Get equipped cosmetics from user
  const equippedCosmetics = card.userId?.equippedCosmetics || {};
  
  // Initialize Stars Parallax background when card-bg-stars-parallax is equipped
  useEffect(() => {
    if (cardRef.current && equippedCosmetics.cardBackground === 'card-bg-stars-parallax') {
      // Small delay to ensure the card is fully rendered
      const timer = setTimeout(() => {
        if (cardRef.current && !parallaxInstanceRef.current) {
          parallaxInstanceRef.current = initStarsParallax(cardRef.current);
        }
      }, 100);
      
      return () => {
        clearTimeout(timer);
        if (parallaxInstanceRef.current) {
          destroyStarsParallax(parallaxInstanceRef.current);
          parallaxInstanceRef.current = null;
        }
      };
    } else {
      // Clean up if background changes
      if (parallaxInstanceRef.current) {
        destroyStarsParallax(parallaxInstanceRef.current);
        parallaxInstanceRef.current = null;
      }
    }
  }, [equippedCosmetics.cardBackground]);
  
  // Helper function to get avatar frame class
  const getAvatarFrameClass = () => {
    if (!equippedCosmetics.avatarFrame) return '';
    const frameId = equippedCosmetics.avatarFrame;
    // Map shop IDs to CSS class names
    const frameMap = {
      'frame-neon-purple': 'avatar-frame-neon-purple',
      'frame-gold-elite': 'avatar-frame-gold-elite',
      'frame-rgb-animated': 'avatar-frame-rgb-animated',
      'frame-fire': 'avatar-frame-fire',
      'frame-ice': 'avatar-frame-ice'
    };
    return frameMap[frameId] || '';
  };
  
  // Helper function to get username effect class
  const getUsernameEffectClass = () => {
    if (!equippedCosmetics.usernameEffect) return '';
    const effectId = equippedCosmetics.usernameEffect;
    const effectMap = {
      'username-rainbow': 'username-rainbow',
      'username-glow-gold': 'username-glow-gold',
      'username-shadow-deep': 'username-shadow-deep',
      'username-neon-cyan': 'username-neon-cyan',
      'username-metallic': 'username-metallic'
    };
    return effectMap[effectId] || '';
  };
  
  // Helper function to get card background class
  const getCardBackgroundClass = () => {
    if (!equippedCosmetics.cardBackground) return '';
    // The ID from the database already matches the CSS class name (e.g., 'card-bg-gradient-sunset')
    return equippedCosmetics.cardBackground;
  };
  
  // Helper function to get card border class
  const getCardBorderClass = () => {
    if (!equippedCosmetics.cardBorder) return '';
    // The ID from the database already matches the CSS class name (e.g., 'card-border-elegant-gold')
    return equippedCosmetics.cardBorder;
  };
  
  // Helper function to get badge icons
  const getBadgeIcons = () => {
    const badgeMap = {
      'badge-og-member': '🏆',
      'badge-giveaway-legend': '🎁',
      'badge-social-star': '⭐',
      'badge-collector': '💎',
      'badge-game-master': '🎮'
    };
    
    if (!equippedCosmetics.badges || equippedCosmetics.badges.length === 0) return [];
    
    return equippedCosmetics.badges.map(badgeId => badgeMap[badgeId] || '').filter(Boolean);
  };

  return (
    <div ref={cardRef} className={`card ${getCardBackgroundClass()} ${getCardBorderClass()}`}>
      <div className="card-body-content">
        {/* Avatar Section - Always present for consistent spacing - Clickable to profile */}
        <div
          className="card-header-image"
          onClick={() => navigate(`/profile/${username}`)}
          style={{ cursor: 'pointer' }}
          title={`View ${card.name}'s profile`}
        >
          {card.avatar ? (
            <img
              src={getImageUrl(card.avatar)}
              alt={card.name}
              className={`card-avatar ${getAvatarFrameClass()}`}
              loading="lazy"
            />
          ) : (
            <div className={`card-avatar-placeholder ${getAvatarFrameClass()}`}>
              <span>{card.name.charAt(0).toUpperCase()}</span>
            </div>
          )}
          
          {/* Display Equipped Badges */}
          {getBadgeIcons().length > 0 && (
            <div className="cosmetic-badges-container">
              {getBadgeIcons().map((icon, index) => (
                <span key={index} className="cosmetic-badge" title="Cosmetic Badge">
                  {icon}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Title and Description - Title is clickable to profile */}
        <div style={{ marginBottom: '12px' }}>
          <h3
            className={`card-title ${getUsernameEffectClass()}`}
            onClick={() => navigate(`/profile/${username}`)}
            style={{ cursor: 'pointer' }}
            title={`View ${card.name}'s profile`}
          >
            {card.name}
          </h3>
          <p className="card-description">
            {card.about}
          </p>
        </div>

        {/* Hashtags Section */}
        {card.hashtags && card.hashtags.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px'
            }}>
              {card.hashtags.map((tag, index) => (
                <span
                  key={index}
                  style={{
                    display: 'inline-block',
                    padding: '3px 10px',
                    background: 'linear-gradient(135deg, var(--btn-secondary), var(--btn-secondary-hover))',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(118, 75, 162, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                  onClick={() => {
                    window.location.href = `/community?hashtag=${tag}`;
                  }}
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Social Links Section - Always present for consistent spacing */}
        <div style={{ marginBottom: '12px' }}>
          <h4 style={{
            color: 'var(--text-primary)',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Social Links
          </h4>
          <div className="card-links">
            {card.socialLinks && card.socialLinks.length > 0 ? (
              card.socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={ensureProtocol(link.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="card-link-badge"
                  style={{ color: 'white' }}
                >
                  {link.platform}
                </a>
              ))
            ) : (
              <span style={{
                color: 'var(--text-secondary)',
                fontSize: '14px',
                fontStyle: 'italic'
              }}>
                No social links added
              </span>
            )}
          </div>
        </div>

        {/* Linktree Section - Always present for consistent spacing */}
        <div style={{ marginBottom: '12px' }}>
          <h4 style={{
            color: 'var(--text-primary)',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Linktree
          </h4>
          {card.linktree ? (
            <a
              href={ensureProtocol(card.linktree)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--btn-secondary)',
                textDecoration: 'none',
                fontWeight: '600',
                fontSize: '13px',
                display: 'inline-flex',
                alignItems: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.color = 'var(--btn-secondary-hover)';
                e.target.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = 'var(--btn-secondary)';
                e.target.style.transform = 'translateX(0)';
              }}
            >
              {card.linktree}
            </a>
          ) : (
            <span style={{
              color: 'var(--text-secondary)',
              fontSize: '13px',
              fontStyle: 'italic'
            }}>
              No linktree added
            </span>
          )}
        </div>

        {/* Support Section - Always present for consistent spacing */}
        <div style={{ marginBottom: '12px' }}>
          <h4 style={{
            color: 'var(--text-primary)',
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Support
          </h4>
          <div className="card-links">
            {card.donationLinks && card.donationLinks.length > 0 ? (
              card.donationLinks.map((link, index) => (
                <a
                  key={index}
                  href={ensureProtocol(link.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="card-link-badge"
                  style={{
                    background: 'linear-gradient(135deg, var(--btn-success), var(--btn-success-hover))',
                    boxShadow: '0 2px 8px rgba(39, 174, 96, 0.3)',
                    color: 'white'
                  }}
                >
                  {link.platform}
                </a>
              ))
            ) : (
              <span style={{
                color: 'var(--text-secondary)',
                fontSize: '13px',
                fontStyle: 'italic'
              }}>
                No donation links added
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Card Actions Section */}
      {/* Card Actions Section */}
      <div className="card-actions">
        {card.userId && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <p style={{
                fontSize: '12px',
                color: 'var(--text-secondary)',
                margin: 0
              }}>
                Created by <span style={{ fontWeight: '600', color: 'var(--text-accent)' }}>{card.userId.username}</span>
              </p>

              {!card.isSelf && user && (
                <button
                  onClick={handleFollow}
                  disabled={followLoading}
                  style={{
                    padding: '2px 8px',
                    fontSize: '11px',
                    borderRadius: '12px',
                    border: isFollowing ? '1px solid var(--border-color)' : 'none',
                    background: isFollowing ? 'transparent' : 'var(--btn-primary)',
                    color: isFollowing ? 'var(--text-secondary)' : 'white',
                    cursor: followLoading ? 'wait' : 'pointer',
                    transition: 'all 0.2s ease',
                    fontWeight: '600'
                  }}
                  onMouseEnter={(e) => {
                    if (isFollowing) {
                      e.target.style.borderColor = 'var(--error-color)';
                      e.target.style.color = 'var(--error-color)';
                      e.target.innerText = 'Unfollow';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isFollowing) {
                      e.target.style.borderColor = 'var(--border-color)';
                      e.target.style.color = 'var(--text-secondary)';
                      e.target.innerText = 'Following';
                    }
                  }}
                >
                  {followLoading ? '...' : (isFollowing ? 'Following' : 'Follow')}
                </button>
              )}
            </div>

            <span className="badge badge-approved" style={{ fontSize: '11px' }}>
              Active
            </span>
          </div>
        )}

        {/* Rating Display */}
        {renderStarRating(card.averageRating)}

        {/* Review Actions */}
        <div className="review-actions-container">
          {user ? (
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowReviewForm(!showReviewForm);
                setShowReviewModal(false);
                setEditingReview(null);
              }}
            >
              {showReviewForm ? '✖ Cancel' : '✍️ Write Review'}
            </button>
          ) : (
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              Log in to write a review
            </p>
          )}

          <button
            className="btn btn-primary"
            onClick={() => {
              setShowReviewModal(true);
              setShowReviewForm(false);
              setEditingReview(null);
            }}
          >
            {`👁️ See Reviews (${card.totalReviews || 0})`}
          </button>
        </div>
      </div>

      {/* Review Form */}
      {showReviewForm && user && (
        <ReviewForm
          targetType="CommunityCard"
          targetId={card._id}
          existingReview={editingReview}
          onSuccess={handleReviewSuccess}
          onCancel={() => {
            setShowReviewForm(false);
            setEditingReview(null);
          }}
        />
      )}

      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        targetType="CommunityCard"
        targetId={card._id}
        currentUserId={user?._id}
        onEditClick={handleEditClick}
      />
    </div>
  );
};

export default CommunityCardItem;
